import { useTranslations } from 'next-intl';
import {
  ContentLayout,
  RentalCompanyPageContent,
} from '@silvertours/common-landingpages-view';
import {
  BenefitsLegacy,
  ContentArticle,
  SerpOffersModule,
  StageLegacy,
} from '@silvertours/front-features';
import { useRawFeatureTranslations } from '@silvertours/front-shared';

import { Body } from '../body';

const RentalCompanyContent = ({
  content: {
    article,
    introduction,
    links,
    location,
    attributes: { partners },
    offersByCategory,
    stage,
    textContent: { primaryHeadline },
  },
  ...commonProps
}: ContentLayout<RentalCompanyPageContent>) => {
  const translations = useRawFeatureTranslations('article');
  const carTypes = useRawFeatureTranslations('offers.car.type');
  const offersTranslations = useTranslations('features.offers');
  const partner = (partners.length && partners[0].name) || '';

  let serpTitle = location.name
    ? offersTranslations('serp.title.partnerDestination')
        .replace('%LOCATION%', location.name)
        .replace('%PARTNER%', partner)
    : offersTranslations('serp.title.partner').replace('%PARTNER%', partner);

  const searchButtonText = partner
    ? offersTranslations('searchButtonTextPartner').replace(
        '%PARTNER%',
        partner,
      )
    : offersTranslations('searchButtonText');

  if (partner === '') {
    serpTitle = offersTranslations('serp.title.default');
  }

  return (
    <Body
      variant={{ kind: 'default' }}
      bodyTop={
        <StageLegacy.Stage
          content={stage.content}
          headline={primaryHeadline}
          variant={{ kind: 'default' }}
        />
      }
      searchData={stage?.content?.search}
      content={({ containerModule }) => [
        containerModule(
          'usp',
          <BenefitsLegacy.Usp translationKey="rentalCompanyUsps" />,
        ),
        containerModule(
          'introduction-content',
          <ContentArticle
            modules={introduction?.segments ?? []}
            translations={translations}
          />,
          !!introduction?.segments,
        ),
        containerModule(
          'serpOffers',
          !!offersByCategory.content && (
            <SerpOffersModule
              content={offersByCategory.content}
              translations={{
                airConditioning: offersTranslations('car.airConditioning'),
                carTypes,
                prevText: offersTranslations('prevText'),
                nextText: offersTranslations('nextText'),
                searchButtonText,
                ariaLabel: offersTranslations('ariaLabel'),
                disclaimertext: offersTranslations('disclaimerSerpOffers'),
                pricePrefix: offersTranslations('pricePrefix'),
                priceSuffix: offersTranslations('priceSuffix'),
                serpTitle,
                seats: offersTranslations('car.seats'),
                doors: offersTranslations('car.doors'),
                transmission: offersTranslations('car.transmission'),
                transmissionAutomatic: offersTranslations(
                  'car.transmissionAutomatic',
                ),
                transmissionManual: offersTranslations(
                  'car.transmissionManual',
                ),
              }}
              searchQuery={
                partner !== ''
                  ? new URLSearchParams(
                      partners[0].partnerType === 'provider'
                        ? { provider: partner }
                        : { supplier: partner },
                    ).toString()
                  : ''
              }
            />
          ),
          !!offersByCategory.content &&
            Object.keys(offersByCategory.content).length > 0,
        ),
        containerModule(
          'article',
          <ContentArticle
            modules={article.segments}
            translations={translations}
            searchQuery={
              partner !== ''
                ? new URLSearchParams(
                    partners[0].partnerType === 'provider'
                      ? { provider: partner }
                      : { supplier: partner },
                  ).toString()
                : ''
            }
          />,
        ),
        containerModule(
          'linkLists',
          <ContentArticle
            modules={links.content ?? []}
            translations={translations}
          />,
          !!links.content.length,
        ),
      ]}
      {...commonProps}
    />
  );
};

export { RentalCompanyContent };
